import * as Sentry from '@sentry/nextjs';
import axios from 'axios';

import { toast } from '@/shared/components/toast';
import {
	ALLOWED_SILENT_API_FAILURES,
	API_ROUTES_TO_RETRY,
	AUTHORIZATION_HEADER,
	PAGE_ID_HEADER,
	TIMEZONE_HEADER,
} from '@/shared/constants/axios-constants';
import { CRITICAL_HTTP_STATUS_CODES } from '@/shared/constants/sentry-constants';
import { reportApiErrorToSentry } from '@/shared/utils/api-error-reporter';
import {
	checkIfNetworkError,
	isAbortedRequest,
	isExpectedRateLimit,
	shouldIgnoreRouteForSentry,
} from '@/shared/utils/axios';
import { parseFastApiError } from '@/shared/utils/error-parser';
import { tabStorage } from '@/shared/utils/tab-storage';

import { getApiErrorDetails, getAppAccessToken } from '@/utils/axios';
import { getCurrentPathWithSearchParams } from '@/utils/common';
import { LOGIN_ROUTE, REDIRECT_PATH } from '@/utils/constants';
import { getUserTimezone } from '@/utils/date';

declare module 'axios' {
	interface AxiosRequestConfig {
		assignmentAuth?: boolean;
		/** Stamped on every request so Sentry can report how long a failed call took. */
		metadata?: { startTime?: number };
	}
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const appAxiosAuthInstance = axios.create({
	baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT_V1,
	withCredentials: true,
});

appAxiosAuthInstance.interceptors.request.use(
	(config) => {
		const pageId = tabStorage.get('selectedWorkspaceId');
		if (pageId) config.headers[PAGE_ID_HEADER] = pageId;
		config.headers[TIMEZONE_HEADER] = getUserTimezone();
		config.metadata = { startTime: Date.now() };

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

appAxiosAuthInstance.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		// Skip processing for aborted requests
		if (axios.isAxiosError(error) && !isAbortedRequest(error)) {
			const status = error.response?.status;

			// Auth failures are reported at every status, not just 5xx — a 401/403 here is
			// exactly the signal that was previously invisible. The reporter filters ignored
			// routes and expected rate limits itself.
			const isSilentAuthFailure = ALLOWED_SILENT_API_FAILURES.some((silentUrl) =>
				error.config?.url?.includes(silentUrl)
			);
			if (!isSilentAuthFailure) {
				reportApiErrorToSentry(error, {
					errorType: 'HTTP Error',
					statusCode: status ?? null,
					source: 'auth',
				});
			}

			if (status === 429) {
				// Only log unexpected rate limits
				if (!isExpectedRateLimit(error)) {
					console.log('Rate limit reached (429) for endpoint:', error.config?.url);
				}
			}
		}
		return Promise.reject(error);
	}
);

export const appAxiosInstance = axios.create({
	baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT_V1,
	withCredentials: true,
});

appAxiosInstance.interceptors.request.use(
	(config) => {
		const pageId = tabStorage.get('selectedWorkspaceId');
		if (pageId) config.headers[PAGE_ID_HEADER] = pageId;
		config.headers[TIMEZONE_HEADER] = getUserTimezone();
		config.metadata = { startTime: Date.now() };

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

appAxiosInstance.interceptors.response.use(
	(response) => {
		return response;
	},
	async (error) => {
		const config = error.config;

		// Retry once on network error after 1 second
		if (
			axios.isAxiosError(error) &&
			!isAbortedRequest(error) &&
			checkIfNetworkError(error) &&
			!error.response &&
			error.request &&
			API_ROUTES_TO_RETRY.includes(config.url) &&
			!config?._retry
		) {
			config._retry = true;
			await sleep(1500);
			return appAxiosInstance(config);
		}

		const { errorMessage, isMessageFromBackend, isCancelledRequest, isNetworkError } =
			getApiErrorDetails(error);

		const isAllowedSilentApiFailure = ALLOWED_SILENT_API_FAILURES.some((url) =>
			config?.url?.includes(url)
		);

		// The single report point for this instance. getApiErrorDetails is pure, and the
		// ~17 feature call sites that also inspect this error must not re-report it.
		if (!isCancelledRequest && !isAllowedSilentApiFailure) {
			reportApiErrorToSentry(error, {
				errorType: isNetworkError ? 'Network Error' : 'HTTP Error',
				statusCode: error.response?.status ?? null,
				source: 'app',
			});
		}

		if (
			!isCancelledRequest &&
			!isNetworkError &&
			(error.response?.status === 400 || error.response?.status === 500) &&
			!isAllowedSilentApiFailure
		) {
			toast({
				title: isMessageFromBackend ? 'Error' : 'Something went wrong',
				description: errorMessage,
				variant: 'destructive',
			});

			error.handled = true;
		}

		if (axios.isAxiosError(error) && !isCancelledRequest) {
			const status = error.response?.status;

			if (status === 401) {
				const isAlreadyOnLogin =
					typeof window !== 'undefined' && window.location.pathname === LOGIN_ROUTE;

				if (config?.assignmentAuth && typeof window !== 'undefined' && !isAlreadyOnLogin) {
					const redirectPathValue = getCurrentPathWithSearchParams();
					const searchParams = new URLSearchParams();
					searchParams.set(REDIRECT_PATH, encodeURIComponent(redirectPathValue));
					window.location.replace(`${LOGIN_ROUTE}?${searchParams.toString()}`);
				}
			} else if (status === 429) {
				// Mark expected rate limits as handled to prevent unhandled promise rejections
				if (isExpectedRateLimit(error)) {
					(error as { handled?: boolean }).handled = true;
				} else {
					// Only log unexpected rate limits
					console.log('Rate limit reached (429) for endpoint:', error.config?.url);
				}
			}

			// Handle 5xx errors with critical severity (in addition to getApiErrorDetails handling)
			// Skip Sentry tracking for ignored routes (e.g., polling endpoints)
			const isIgnoredRoute = shouldIgnoreRouteForSentry(error.config?.url);
			if (
				status &&
				CRITICAL_HTTP_STATUS_CODES.includes(status) &&
				!isIgnoredRoute &&
				!isAllowedSilentApiFailure
			) {
				Sentry.addBreadcrumb({
					category: 'api',
					message: `Critical 5xx error on ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
					level: 'error',
					data: {
						status,
						statusText: error.response?.statusText,
					},
				});
			}
		}
		return Promise.reject(error);
	}
);

// The agent backend is on a separate subdomain and requires token auth.
// The token is fetched lazily via getAppAccessToken() (server action +
// in-memory cache) so the HttpOnly cookie never needs to be read in client JS.

export const agentAxiosInstance = axios.create({
	baseURL: process.env.NEXT_PUBLIC_AI_AGENT_API_ENDPOINT,
});

agentAxiosInstance.interceptors.request.use(async (config) => {
	const token = await getAppAccessToken();
	if (token) config.headers[AUTHORIZATION_HEADER] = `${token}`;
	const pageId = tabStorage.get('selectedWorkspaceId');
	if (pageId) config.headers[PAGE_ID_HEADER] = pageId;
	config.headers[TIMEZONE_HEADER] = getUserTimezone();
	config.metadata = { startTime: Date.now() };
	return config;
});

// The agent backend uses FastAPI error shapes, so report and surface those errors
// here instead of requiring every agent caller to implement its own toast.
agentAxiosInstance.interceptors.response.use(
	(response) => response,
	(error) => {
		if (axios.isAxiosError(error) && !isAbortedRequest(error)) {
			const { isCancelledRequest, isNetworkError } = getApiErrorDetails(error);
			const status = error.response?.status;
			const hasCallerSpecificHandling = status === 404 || status === 409 || status === 429;

			if (!isCancelledRequest) {
				reportApiErrorToSentry(error, {
					errorType: isNetworkError ? 'Network Error' : 'HTTP Error',
					statusCode: status ?? null,
					source: 'agent',
				});
			}

			if (!isCancelledRequest && !isNetworkError && !hasCallerSpecificHandling) {
				toast({
					title: 'Something went wrong',
					description: parseFastApiError(error),
					variant: 'destructive',
				});
				(error as { handled?: boolean }).handled = true;
			}
		}
		return Promise.reject(error);
	}
);
